import React from 'react';
import { CircuitBreakerState } from '../../types';

interface CircuitBreakerIndicatorProps {
  queueId: number;
  circuitState?: CircuitBreakerState;
}

/**
 * CircuitBreakerIndicator - Displays the status of a queue's circuit breaker
 *
 * Shows:
 * - "Active" (green) when circuit is CLOSED (normal project)
 * - "Paused" (yellow) when circuit is OPEN (circuit tripped)
 * - "Disabled" (gray) when circuit breaker feature is off
 * - "Unknown" when state is not available
 */
const CircuitBreakerIndicator: React.FC<CircuitBreakerIndicatorProps> = ({
  queueId,
  circuitState,
}) => {
  // Handle undefined/null state
  if (!circuitState) {
    return (
      <div
        data-testid="circuit-indicator"
        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700"
      >
        <span className="mr-1">&#9679;</span>
        <span>Unknown</span>
      </div>
    );
  }

  // Handle disabled circuit breaker
  if (!circuitState.enabled) {
    return (
      <div
        data-testid="circuit-indicator"
        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700"
      >
        <span className="mr-1">&#9679;</span>
        <span>Disabled</span>
      </div>
    );
  }

  // Handle OPEN state (circuit tripped, queue paused)
  if (circuitState.state === 'OPEN') {
    const openedAt = circuitState.opened_at
      ? new Date(circuitState.opened_at)
      : null;

    const formatDate = (date: Date | null): string => {
      if (!date) return '';
      return date.toISOString().split('T')[0];
    };

    return (
      <div
        data-testid="circuit-indicator"
        className="inline-flex flex-col items-start text-yellow-600 dark:text-yellow-400"
      >
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300">
          <span className="mr-1">&#9679;</span>
          <span>Paused</span>
        </div>
        {circuitState.reason && (
          <div className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
            <span className="font-medium">Reason: </span>
            {circuitState.reason}
          </div>
        )}
        {openedAt && (
          <div className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
            <span className="font-medium">Opened: </span>
            {formatDate(openedAt)}
          </div>
        )}
      </div>
    );
  }

  // Handle CLOSED state (active, normal project)
  return (
    <div
      data-testid="circuit-indicator"
      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30"
    >
      <span className="mr-1">&#9679;</span>
      <span>Active</span>
    </div>
  );
};

export default CircuitBreakerIndicator;
